import React, { useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Select,
  Space,
  Popconfirm,
  message,
  Typography,
  Tag,
  Card,
  Tabs,
  Row,
  Col,
  DatePicker,
  InputNumber,
  Upload,
  Image
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  StarOutlined,
  StarFilled,
  UploadOutlined,
  CheckCircleOutlined,
  PictureOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { articlesApi, uploadApi } from '../../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface Article {
  _id: string;
  title: { en: string; zh: string };
  slug: string;
  excerpt: { en: string; zh: string };
  content: { en: string; zh: string };
  featuredImage?: {
    // legacy
    url?: string;
    alt?: { en?: string; zh?: string };
    // localized
    en?: { url?: string; alt?: string };
    zh?: { url?: string; alt?: string };
  };
  author: {
    name: string;
    bio?: { en: string; zh: string };
    avatar?: string;
  };
  category: { en: string; zh: string };
  tags: Array<{ en: string; zh: string }>;
  isPublished: boolean;
  publishedAt?: string;
  isFeatured: boolean;
  readingTime: number;
  viewCount: number;
  likeCount: number;
  seoTitle?: { en: string; zh: string };
  seoDescription?: { en: string; zh: string };
  relatedProducts: string[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

const AdminArticles: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingArticle, setEditingArticle] = useState<Article | null>(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // Fetch articles
  const { data: articlesData, isLoading } = useQuery({
    queryKey: ['admin-articles'],
    queryFn: () => articlesApi.getAll({ admin: true })
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: articlesApi.create,
    onSuccess: (data) => {
      console.log('Article created successfully:', data);
      message.success('Article created successfully');
      setIsModalVisible(false);
      form.resetFields();
      setEditingArticle(null);
      queryClient.invalidateQueries({ queryKey: ['admin-articles'] });
    },
    onError: (error: any) => {
      console.error('Create article error:', error);
      console.error('Error response:', error.response?.data);
      message.error(error.response?.data?.message || 'Failed to create article');
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => {
      console.log('Updating article with ID:', id);
      console.log('Update data:', data);
      return articlesApi.update(id, data);
    },
    onSuccess: (data) => {
      console.log('Article updated successfully:', data);
      message.success('Article updated successfully');
      setIsModalVisible(false);
      form.resetFields();
      setEditingArticle(null);
      queryClient.invalidateQueries({ queryKey: ['admin-articles'] });
    },
    onError: (error: any) => {
      console.error('Update article error:', error);
      console.error('Error response:', error.response?.data);
      message.error(error.response?.data?.message || 'Failed to update article');
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: articlesApi.delete,
    onSuccess: () => {
      message.success('Article deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['admin-articles'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to delete article');
    }
  });

  // Toggle published mutation
  const togglePublishedMutation = useMutation({
    mutationFn: articlesApi.togglePublished,
    onSuccess: () => {
      message.success('Article status updated');
      queryClient.invalidateQueries({ queryKey: ['admin-articles'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update status');
    }
  });

  // Toggle featured mutation
  const toggleFeaturedMutation = useMutation({
    mutationFn: articlesApi.toggleFeatured,
    onSuccess: () => {
      message.success('Article featured status updated');
      queryClient.invalidateQueries({ queryKey: ['admin-articles'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update featured status');
    }
  });

  // Image upload handler
  const handleImageUpload = async (file: File): Promise<string> => {
    console.log('Starting image upload for file:', file.name);
    setUploadLoading(true);
    try {
      const response = await uploadApi.uploadImage(file);
      console.log('Image upload response:', response);
      console.log('Image URL from response:', response.file.url);
      console.log('Full file object:', response.file);
      message.success('Image uploaded successfully');
      return response.file.url;
    } catch (error: any) {
      console.error('Image upload error:', error);
      console.error('Upload error response:', error.response?.data);
      message.error(error.response?.data?.message || 'Failed to upload image');
      throw error;
    } finally {
      setUploadLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingArticle(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (article: Article) => {
    console.log('=== EDITING ARTICLE ===');
    console.log('Full article object:', article);
    console.log('Article content:', article.content);
    console.log('Article featuredImage:', article.featuredImage);
    
    setEditingArticle(article);
    
    const formData = {
      title: article.title,
      excerpt: article.excerpt,
      content: article.content,
      author: article.author,
      category: article.category,
      tags: article.tags,
      isPublished: article.isPublished,
      publishedAt: article.publishedAt ? dayjs(article.publishedAt) : null,
      isFeatured: article.isFeatured,
      readingTime: article.readingTime,
      seoTitle: article.seoTitle,
      seoDescription: article.seoDescription,
      sortOrder: article.sortOrder,
      // Initialize localized featuredImage fields
      featuredImage: {
        url: article.featuredImage?.url || '',
        alt: {
          en: article.featuredImage?.alt?.en || '',
          zh: article.featuredImage?.alt?.zh || ''
        },
        en: {
          url: article.featuredImage?.en?.url || '',
          alt: article.featuredImage?.en?.alt || ''
        },
        zh: {
          url: article.featuredImage?.zh?.url || '',
          alt: article.featuredImage?.zh?.alt || ''
        }
      }
    };
    
    console.log('Form data being set:', formData);
    console.log('Content in form data:', formData.content);
    console.log('FeaturedImage in form data:', formData.featuredImage);
    console.log('FeaturedImage URL being set:', formData.featuredImage.url);
    
    form.setFieldsValue(formData);
    
    // Verify the form actually got the values
    setTimeout(() => {
      const currentFormValues = form.getFieldsValue();
      console.log('Current form values after setting:', currentFormValues);
      console.log('Content field in form after setting:', form.getFieldValue(['content']));
      console.log('FeaturedImage URL in form after setting:', form.getFieldValue(['featuredImage', 'url']));
      console.log('FeaturedImage alt.en in form after setting:', form.getFieldValue(['featuredImage', 'alt', 'en']));
      console.log('FeaturedImage alt.zh in form after setting:', form.getFieldValue(['featuredImage', 'alt', 'zh']));
    }, 100);
    
    setIsModalVisible(true);
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      console.log('Form values before submission:', values);
      console.log('Featured image in form:', values.featuredImage);
      console.log('Is editing article:', !!editingArticle);
      if (editingArticle) {
        console.log('Editing article ID:', editingArticle._id);
      }
      
      // Detailed logging for debugging
      console.log('Raw form values:');
      console.log('- title:', values.title);
      console.log('- excerpt:', values.excerpt);
      console.log('- content:', values.content);
      console.log('- author:', values.author);
      console.log('- category:', values.category);
      console.log('- featuredImage:', values.featuredImage);
      if (values.featuredImage) {
        console.log('  featuredImage.url:', values.featuredImage.url);
        console.log('  featuredImage.alt.en:', values.featuredImage.alt?.en);
        console.log('  featuredImage.alt.zh:', values.featuredImage.alt?.zh);
      }
      console.log('- tags:', values.tags);
      console.log('- isPublished:', values.isPublished);
      console.log('- isFeatured:', values.isFeatured);
      
      // Transform data to match backend schema
      const transformedData = {
        ...values,
        // Ensure tags is an array (empty if not provided)
        tags: values.tags || [],
        // Ensure author has bio structure even if empty
        author: {
          ...values.author,
          bio: values.author?.bio || { en: '', zh: '' }
        },
        // Handle featuredImage with localized structure
        ...(values.featuredImage ? {
          featuredImage: {
            url: values.featuredImage.url, // keep legacy if present
            alt: values.featuredImage.alt,
            en: values.featuredImage.en,
            zh: values.featuredImage.zh
          }
        } : {})
      };
      
      console.log('Transformed data:', transformedData);
      console.log('Content in transformed data:', transformedData.content);
      console.log('FeaturedImage in transformed data:', transformedData.featuredImage);
      
      if (editingArticle) {
        updateMutation.mutate({ id: editingArticle._id, data: transformedData });
      } else {
        createMutation.mutate(transformedData);
      }
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const columns = [
    {
      title: 'Featured Image',
      dataIndex: 'featuredImage',
      key: 'featuredImage',
      width: 80,
      render: (image: any) => {
        const previewUrl = image?.en?.url || image?.url; // prefer EN localized for admin listing
        return previewUrl ? (
          <Image
            width={50}
            height={35}
            src={previewUrl}
            alt={image?.en?.alt || image?.alt?.en || 'Article image'}
            style={{ objectFit: 'cover', borderRadius: 4 }}
          />
        ) : (
          <div className="w-12 h-8 bg-gray-200 rounded flex items-center justify-center">
            <EyeOutlined className="text-gray-400" />
          </div>
        )
      }
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (title: { en: string; zh: string }) => (
        <div>
          <div className="font-medium">{title.en}</div>
          <div className="text-sm text-gray-500">{title.zh}</div>
        </div>
      )
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category: { en: string; zh: string }) => (
        <Tag color="blue">{category.en}</Tag>
      )
    },
    {
      title: 'Author',
      dataIndex: 'author',
      key: 'author',
      render: (author: { name: string }) => author.name
    },
         {
       title: 'Status',
       key: 'status',
       render: (_: any, record: Article) => (
         <Space direction="vertical" size="small">
           <Tag color={record.isPublished ? 'green' : 'orange'}>
             {record.isPublished ? 'Published' : 'Draft'}
           </Tag>
           {record.isFeatured && <Tag color="gold">Featured</Tag>}
         </Space>
       )
     },
     {
       title: 'Stats',
       key: 'stats',
       render: (_: any, record: Article) => (
         <Space direction="vertical" size="small">
           <Text type="secondary">👁 {record.viewCount}</Text>
           <Text type="secondary">👍 {record.likeCount}</Text>
           <Text type="secondary">⏱ {record.readingTime}min</Text>
         </Space>
       )
     },
    {
      title: 'Published',
      dataIndex: 'publishedAt',
      key: 'publishedAt',
      render: (date: string) => date ? dayjs(date).format('MMM D, YYYY') : '-'
    },
         {
       title: 'Actions',
       key: 'actions',
       render: (_: any, record: Article) => (
         <Space>
           <Button
             icon={<EditOutlined />}
             onClick={() => handleEdit(record)}
             type="text"
           />
           <Button
             icon={record.isPublished ? <CheckCircleOutlined /> : <EyeOutlined />}
             onClick={() => togglePublishedMutation.mutate(record._id)}
             type="text"
             title={record.isPublished ? 'Unpublish' : 'Publish'}
           />
           <Button
             icon={record.isFeatured ? <StarFilled /> : <StarOutlined />}
             onClick={() => toggleFeaturedMutation.mutate(record._id)}
             type="text"
             title={record.isFeatured ? 'Unfeature' : 'Feature'}
           />
           <Popconfirm
             title="Are you sure you want to delete this article?"
             onConfirm={() => handleDelete(record._id)}
             okText="Yes"
             cancelText="No"
           >
             <Button icon={<DeleteOutlined />} type="text" danger />
           </Popconfirm>
         </Space>
       )
     }
  ];

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="flex justify-between items-center mb-6">
        <Title level={2}>Articles Management</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          size="large"
        >
          Add Article
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={articlesData?.articles || []}
          loading={isLoading}
          rowKey="_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} articles`
          }}
        />
      </Card>

      <Modal
        title={editingArticle ? 'Edit Article' : 'Add New Article'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          setEditingArticle(null);
        }}
        onOk={handleSubmit}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        width={1000}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            isPublished: false,
            isFeatured: false,
            readingTime: 5,
            sortOrder: 0,
            tags: [],
            featuredImage: {
              url: '',
              alt: {
                en: '',
                zh: ''
              }
            }
          }}
        >
          <Tabs>
            <Tabs.TabPane tab="Basic Info" key="basic">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name={['title', 'en']}
                    label="Title (English)"
                    rules={[{ required: true, message: 'Please enter English title' }]}
                  >
                    <Input placeholder="Enter article title in English" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name={['title', 'zh']}
                    label="Title (Chinese)"
                    rules={[{ required: true, message: 'Please enter Chinese title' }]}
                  >
                    <Input placeholder="輸入中文標題" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name={['excerpt', 'en']}
                    label="Excerpt (English)"
                    rules={[{ required: true, message: 'Please enter English excerpt' }]}
                  >
                    <TextArea rows={3} placeholder="Brief description in English" maxLength={300} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name={['excerpt', 'zh']}
                    label="Excerpt (Chinese)"
                    rules={[{ required: true, message: 'Please enter Chinese excerpt' }]}
                  >
                    <TextArea rows={3} placeholder="中文摘要" maxLength={300} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name={['category', 'en']}
                    label="Category (English)"
                    rules={[{ required: true, message: 'Please enter English category' }]}
                  >
                    <Select placeholder="Select or enter category">
                      <Option value="Health Tips">Health Tips</Option>
                      <Option value="Product Reviews">Product Reviews</Option>
                      <Option value="Medical News">Medical News</Option>
                      <Option value="Wellness">Wellness</Option>
                      <Option value="Company News">Company News</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name={['category', 'zh']}
                    label="Category (Chinese)"
                    rules={[{ required: true, message: 'Please enter Chinese category' }]}
                  >
                    <Select placeholder="選擇或輸入分類">
                      <Option value="健康貼士">健康貼士</Option>
                      <Option value="產品評論">產品評論</Option>
                      <Option value="醫療新聞">醫療新聞</Option>
                      <Option value="健康">健康</Option>
                      <Option value="公司新聞">公司新聞</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name={['author', 'name']}
                label="Author Name"
                rules={[{ required: true, message: 'Please enter author name' }]}
              >
                <Input placeholder="Author full name" />
              </Form.Item>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Content" key="content">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name={['content', 'en']}
                    label="Content (English)"
                    rules={[{ required: true, message: 'Please enter English content' }]}
                  >
                    <TextArea rows={10} placeholder="Article content in English" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name={['content', 'zh']}
                    label="Content (Chinese)"
                    rules={[{ required: true, message: 'Please enter Chinese content' }]}
                  >
                    <TextArea rows={10} placeholder="中文文章內容" />
                  </Form.Item>
                </Col>
              </Row>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Settings" key="settings">
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="isPublished" label="Published" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="isFeatured" label="Featured" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="readingTime" label="Reading Time (minutes)">
                    <InputNumber min={1} max={60} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="publishedAt" label="Publish Date">
                    <DatePicker showTime style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="sortOrder" label="Sort Order">
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </Tabs.TabPane>

            <Tabs.TabPane tab="SEO" key="seo">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name={['seoTitle', 'en']}
                    label="SEO Title (English)"
                  >
                    <Input placeholder="SEO title for search engines" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name={['seoTitle', 'zh']}
                    label="SEO Title (Chinese)"
                  >
                    <Input placeholder="中文SEO標題" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name={['seoDescription', 'en']}
                    label="SEO Description (English)"
                  >
                    <TextArea rows={3} placeholder="Meta description for search engines" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name={['seoDescription', 'zh']}
                    label="SEO Description (Chinese)"
                  >
                    <TextArea rows={3} placeholder="中文SEO描述" />
                  </Form.Item>
                </Col>
              </Row>
            </Tabs.TabPane>

            <Tabs.TabPane tab={<span><PictureOutlined />Featured Image</span>} key="image">
              <div>
                <Title level={5}>Featured Image</Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                  Upload separate featured images for English and Chinese. Recommended size: 800x600px.
                </Text>

                {/* English uploader */}
                <Card size="small" title="English Image" style={{ marginBottom: 16 }}>
                  <Form.Item shouldUpdate={(p, c) => (p?.featuredImage?.en?.url !== c?.featuredImage?.en?.url)}>
                    {({ getFieldValue }) => {
                      const url = getFieldValue(['featuredImage','en','url']);
                      const alt = getFieldValue(['featuredImage','en','alt']);
                      return url ? (
                        <div style={{ marginBottom: 16 }}>
                          <Image width={200} height={150} src={url} alt={alt || 'Featured image (EN)'} style={{ objectFit: 'cover', borderRadius: 4 }} />
                          <div style={{ marginTop: 8 }}>
                            <Button type="text" danger onClick={() => { form.setFieldValue(['featuredImage','en','url'], ''); form.setFieldValue(['featuredImage','en','alt'], ''); }}>Remove Image</Button>
                          </div>
                        </div>
                      ) : null;
                    }}
                  </Form.Item>
                  <Upload
                    name="image"
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={async (file) => {
                      try {
                        const imageUrl = await handleImageUpload(file);
                        form.setFieldValue(['featuredImage','en','url'], imageUrl);
                        const curAlt = form.getFieldValue(['featuredImage','en','alt']);
                        if (!curAlt) form.setFieldValue(['featuredImage','en','alt'], 'Article featured image');
                      } catch (e) {
                        console.error(e);
                      }
                      return false;
                    }}
                  >
                    <Button icon={<UploadOutlined />} loading={uploadLoading} type="dashed" style={{ width: '100%', height: 80 }}>
                      {uploadLoading ? 'Uploading...' : 'Upload English Image'}
                    </Button>
                  </Upload>
                  <Form.Item name={['featuredImage','en','url']} style={{ display: 'none' }}><Input type="hidden" /></Form.Item>
                  <Form.Item name={['featuredImage','en','alt']} label="Alt Text (English)"><Input placeholder="Describe the image in English" /></Form.Item>
                </Card>

                {/* Chinese uploader */}
                <Card size="small" title="Chinese Image">
                  <Form.Item shouldUpdate={(p, c) => (p?.featuredImage?.zh?.url !== c?.featuredImage?.zh?.url)}>
                    {({ getFieldValue }) => {
                      const url = getFieldValue(['featuredImage','zh','url']);
                      const alt = getFieldValue(['featuredImage','zh','alt']);
                      return url ? (
                        <div style={{ marginBottom: 16 }}>
                          <Image width={200} height={150} src={url} alt={alt || '特色圖片 (ZH)'} style={{ objectFit: 'cover', borderRadius: 4 }} />
                          <div style={{ marginTop: 8 }}>
                            <Button type="text" danger onClick={() => { form.setFieldValue(['featuredImage','zh','url'], ''); form.setFieldValue(['featuredImage','zh','alt'], ''); }}>移除圖片</Button>
                          </div>
                        </div>
                      ) : null;
                    }}
                  </Form.Item>
                  <Upload
                    name="image"
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={async (file) => {
                      try {
                        const imageUrl = await handleImageUpload(file);
                        form.setFieldValue(['featuredImage','zh','url'], imageUrl);
                        const curAlt = form.getFieldValue(['featuredImage','zh','alt']);
                        if (!curAlt) form.setFieldValue(['featuredImage','zh','alt'], '文章特色圖片');
                      } catch (e) {
                        console.error(e);
                      }
                      return false;
                    }}
                  >
                    <Button icon={<UploadOutlined />} loading={uploadLoading} type="dashed" style={{ width: '100%', height: 80 }}>
                      {uploadLoading ? '上載中...' : '上載中文圖片'}
                    </Button>
                  </Upload>
                  <Form.Item name={['featuredImage','zh','url']} style={{ display: 'none' }}><Input type="hidden" /></Form.Item>
                  <Form.Item name={['featuredImage','zh','alt']} label="Alt Text (Chinese)"><Input placeholder="中文圖片描述" /></Form.Item>
                </Card>
              </div>
            </Tabs.TabPane>
          </Tabs>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminArticles;
